import { useCallback, useEffect, useRef, useState } from 'react';
import {
  formatDoneCount,
  formatRemainingInMission,
  type CampaignFrise as CampaignFriseData,
} from '../systems/campaign-frise';
import { isTypingInEditableField } from '../systems/keyboard-utils';
import { CampaignFrise } from './campaign-frise';
import styles from './chapter-progress-card.module.css';

/** Durée du fondu de sortie, avant le bilan. */
const EXIT_MS = 320;

export interface ChapterProgressCardProps {
  frise: CampaignFriseData;
  /** Décor de la dernière scène, déjà résolu en URL (image uniquement). */
  backgroundUrl?: string;
  onContinue: () => void;
}

/**
 * Fin de chapitre : le cran du chapitre se remplit dans la frise de campagne, avant le
 * bilan. Espace, Entrée ou un clic mènent au bilan.
 */
export function ChapterProgressCard({ frise, backgroundUrl, onContinue }: ChapterProgressCardProps) {
  const [exiting, setExiting] = useState(false);
  const exitTimerRef = useRef<number | null>(null);

  const handleContinue = useCallback(() => {
    if (exitTimerRef.current !== null) return;
    setExiting(true);
    exitTimerRef.current = window.setTimeout(onContinue, EXIT_MS);
  }, [onContinue]);

  useEffect(() => () => {
    if (exitTimerRef.current !== null) window.clearTimeout(exitTimerRef.current);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isTypingInEditableField()) return;
      if (e.key !== ' ' && e.key !== 'Enter') return;
      e.preventDefault();
      handleContinue();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleContinue]);

  return (
    <div
      className={`${styles.cadre} ${backgroundUrl ? styles.decor : styles.nuit} ${exiting ? styles.sortie : ''}`}
      style={backgroundUrl ? { backgroundImage: `url("${backgroundUrl}")` } : undefined}
      onClick={handleContinue}
      role="dialog"
      aria-modal="true"
      aria-labelledby="chapter-progress-title"
    >
      <div className={`${styles.fin} cn-zoom-ui`}>
        <div className={styles.bandeau}>
          Mission {frise.missionPosition} · fin du chapitre {frise.chapterPosition}{' '}
          <span className={styles.de}>sur {frise.chapterCount}</span>
        </div>
        <h2 id="chapter-progress-title" className={styles.titre}>{frise.currentChapterTitle}</h2>
        <CampaignFrise frise={frise} variant="fin" className={styles.frise} />
        <p className={styles.dit}>
          {formatRemainingInMission(frise)}
          <span>{formatDoneCount(frise)}</span>
        </p>
        <div className={styles.pied}>
          <div className={styles.trait} />
          <div className={styles.quoi}>Voir le bilan</div>
          <span className={styles.touche}>Espace</span>
        </div>
      </div>
    </div>
  );
}
